import { useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AttachmentOwnerType,
  UserRole,
  OPERATIONS_ROLES,
  formatRobotShortId,
  type DeploymentSummaryDto,
} from '@bhops/shared';
import { canEditAny } from '@/permissions';
import { attachmentsApi, type AttachmentDto, uploadAttachment } from '@/api/attachments';
import { deploymentsApi } from '@/api/deployments';
import {
  customerMasterApi,
  CUSTOMER_MASTER_STATUS_COLOR,
  CUSTOMER_MASTER_STATUS_LABEL,
  type CustomerMasterRow,
  type CustomerMasterStatus,
} from '@/api/customer-master';
import { signatureDocumentsApi } from '@/api/signatureDocuments';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
// 추가수정 2차(26-07-27) 정정4 — 이 페이지의 유일한 날짜 표시(서명 완료 문서의
// 완료일)를 KST 정본으로. toYmd 는 ISO 를 UTC 로 자르는 헬퍼라 여기서는 더 쓰지
// 않는다(이 파일에 다른 날짜 표시는 없다 — 설치 사진 타일은 파일명만 보여준다).
import { kstYmd } from '@/lib/format-date';

// S1 Phase 8 (원본 #5 + R2 #6) — 사이드바 '고객사 서류' 탭.
//
// 읽기 전용 페이지. 좌측 고객사 검색 + 목록 + 우측 3 첨부 카테고리
// (서명서·설치 사진 / 간택기 연결 / 인수교육 확인증). 필드팀이 PWA 로 업로드한
// 서류가 여기에 표시됨. 카테고리는 attachments owner type 으로 매핑
// (ownerId = Customer.id). 외부 협력업체 토큰 URL 은 S5b, PWA 업로드 wire-up
// 은 S5a — 본 페이지는 read-only 로 표시만 담당.
//
// docs/superpowers/plans/2026-05-29-s1-business-ux-comprehensive.md Phase 8

// 고객사 서류 정리(2026-07) — 카드는 '서명 완료 문서' + '설치 사진' 둘만. 가스 연결
// 사진·인수교육 확인증·인수증 카드는 제거(요구사항). 설치 사진은 attachments owner
// type CUSTOMER_MASTER_INSTALL 로 조회.
const DOCUMENT_CATEGORIES = [
  { ownerType: AttachmentOwnerType.CUSTOMER_MASTER_INSTALL, label: '설치 사진' },
] as const;

// 권한 통합(오너 결정 2026-08-27) — "권한이 없어서 보지 못했던 탭도 오픈".
// 종전엔 사업팀(ADMIN/MANAGER/SALES_MANAGER)만 접근할 수 있었다 → 운영 권한 전체.
// 라우트(router.tsx)와 **같은 상수**를 쓴다 — 이중 방어이되 값은 한 벌이다.
const ALLOWED_ROLES: readonly UserRole[] = OPERATIONS_ROLES;

// round11 (요구 12) — 검색 전용 picker 대신 목록에서 클릭 선택. 서버 pageSize
// 상한(200)까지 한 번에 받아 검색은 client-side 로 필터한다(현 데이터 국내 99 ·
// 해외 9). total 이 이 값을 넘으면 목록 하단에 잘림을 알린다.
const LIST_PAGE_SIZE = 200;

/**
 * 다계약 표시 차수3(26-08-05) ㉡ — 이 화면의 **정렬은 이 화면이 정한다**.
 *
 * 🔴 무엇이 틀렸었나 — 종전엔 sort 를 보내지 않아 **서버 기본값**을 받았다. 그 기본값이 이번
 *   차수에 '등록 최근순' → '처음 계약일 오름차순'으로 바뀌면서 이 화면이 조용히 휩쓸렸다:
 *   방금 계약·설치해 서류를 올려야 하는 고객사가 목록 맨 아래로 가고, 고객사가 200곳
 *   (pageSize 상한)을 넘으면 **목록에 아예 없다**. 서버 기본값은 시트가 오너와 합의한
 *   값이고 이 화면의 용도가 아니다 — 남의 기본값에 자기 순서를 맡기지 않는다.
 *
 * 'recent'(등록 최근순)를 고른 근거 — 이 화면의 용도는 **방금 등록·계약·설치한 고객사**의
 * 서류를 확인·정리하는 것이다. 등록이 최근인 순서는 그 고객사를 늘 첫 화면 맨 위에 두고,
 * 잘림(상한 200)이 생겨도 **잘려 나가는 쪽이 오래된 고객사**라 이 화면의 일이 막히지 않는다.
 * (가나다순은 새 고객사의 위치가 이름에 따라 아무 데나 놓이고, 처음 계약일 오름차순은 새
 *  고객사를 정확히 맨 뒤 — 잘림의 첫 희생자 — 로 보낸다.)
 */
const LIST_SORT = 'recent' as const;

// 추가수정(26-07-27) §2-2 — 국내/해외 탭. CustomerMasterPage 의 ScopeTabButton
// 패턴을 로컬 복제(공용화는 세 번째 사용처 등장 시 — AutoApplyToggle 전례와
// 동일 기준). role=tab + ArrowLeft/Right/Home/End 탐색 동일, 엘리먼트 id 만
// cd-scope-tab-* 로 구분. 구분 기준은 서버 scope 파라미터(KR=국내) 그대로.
/**
 * 상태 거르기(2026-10-01 오너 요청) — 칩 순서는 일의 흐름(체결 → 계약 → 설치 → 회수 → 종료),
 * '미정'은 끝. 목록에 있는 상태만 칩으로 보인다('종료'는 '종료 포함'을 켰을 때만 목록에 온다).
 */
const STATUS_FILTER_ORDER: readonly CustomerMasterStatus[] = [
  'in_negotiation',
  'contracted',
  'install_ready',
  'install_done',
  'return_planned',
  'retired',
  'undefined',
];
type StatusFilter = CustomerMasterStatus | 'all';

/**
 * 정렬(2026-10-01 오너 요청) — 화면에서 다시 줄 세운다. 서버에서는 늘 '등록 최근순'(LIST_SORT)으로
 * 받는다: 잘림(상한 200)이 생겨도 빠지는 쪽이 오래된 고객사여야 한다는 근거는 그대로라, 정렬을
 * 바꿀 때 서버 순서는 건드리지 않는다. '오래된 등록순'은 받은 순서를 뒤집은 것이다.
 */
const SORT_OPTIONS = [
  { key: 'recent', label: '최신 등록순' },
  { key: 'oldest', label: '오래된 등록순' },
  { key: 'name', label: '가나다순' },
] as const;
type SortKey = (typeof SORT_OPTIONS)[number]['key'];
const nameOf = (r: CustomerMasterRow) => (r.displayName || r.legalName || '').trim();

const SCOPE_TABS = ['domestic', 'overseas'] as const;
type ScopeTab = (typeof SCOPE_TABS)[number];

function ScopeTabButton({
  scopeKey,
  active,
  onActivate,
  children,
}: {
  scopeKey: ScopeTab;
  active: boolean;
  onActivate: () => void;
  children: ReactNode;
}) {
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    const idx = SCOPE_TABS.indexOf(scopeKey);
    let next: ScopeTab | null = null;
    if (e.key === 'ArrowRight') next = SCOPE_TABS[(idx + 1) % SCOPE_TABS.length];
    else if (e.key === 'ArrowLeft')
      next = SCOPE_TABS[(idx - 1 + SCOPE_TABS.length) % SCOPE_TABS.length];
    else if (e.key === 'Home') next = SCOPE_TABS[0];
    else if (e.key === 'End') next = SCOPE_TABS[SCOPE_TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    document.getElementById(`cd-scope-tab-${next}`)?.focus();
  }

  return (
    <button
      type="button"
      role="tab"
      id={`cd-scope-tab-${scopeKey}`}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      style={{
        padding: '8px 14px',
        border: 'none',
        background: 'transparent',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--fg-brand)' : 'var(--fg-muted)',
        borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

export function CustomerDocumentsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canView = !!role && ALLOWED_ROLES.includes(role);
  // 권한 통합(오너 결정 2026-08-27) — 삭제·완전삭제도 운영 권한 전체에 연다.
  // 종전 주석은 "ADMIN 전용의 최종 게이트는 이 UI 노출"이라고 적혀 있었다. 서버가
  // 열린 지금 그 문장을 그대로 두면 다음 사람이 없는 경계를 믿는다.
  // 이름(isAdmin)은 호출부가 많아 유지한다 — 뜻은 '지울 수 있는 사람'이다.
  const isAdmin = !!role && canEditAny(role);
  const [customer, setCustomer] = useState<CustomerMasterRow | null>(null);
  const [search, setSearch] = useState('');
  // round10 (요구 5) 와 동형 — '종료'(retired) 고객사 기본 숨김 + 토글.
  const [includeRetired, setIncludeRetired] = useState(false);
  // 추가수정(26-07-27) §2-2 — 국내/해외 탭 상태. 기본 국내. 탭 전환은 목록
  // 조회 scope 만 바꾸고, 선택된 고객사(우측 서류 영역)는 그대로 유지한다.
  const [scope, setScope] = useState<ScopeTab>('domestic');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('recent');

  const listQ = useQuery({
    // 추가수정(26-07-27) §2-2 — queryKey 에 scope 포함(탭별 캐시 분리).
    queryKey: ['customer-master', 'documents-picker', scope, includeRetired],
    queryFn: () =>
      customerMasterApi.list({
        scope,
        // 다계약 표시 차수3(26-08-05) ㉡ — 순서를 **명시**한다(근거는 LIST_SORT 주석).
        sort: LIST_SORT,
        pageSize: LIST_PAGE_SIZE,
        includeRetired: includeRetired || undefined,
      }),
    enabled: canView,
    staleTime: 60_000,
  });

  const rows = useMemo(() => listQ.data?.items ?? [], [listQ.data]);
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.displayName ?? ''} ${r.legalName ?? ''}`.toLowerCase().includes(q),
    );
  }, [rows, search]);
  // 칩 옆 숫자는 검색까지 반영한 개수 — 누르면 그 숫자만큼 남는다.
  const statusCounts = useMemo(() => {
    const m = new Map<CustomerMasterStatus, number>();
    for (const r of searched) m.set(r.status, (m.get(r.status) ?? 0) + 1);
    return m;
  }, [searched]);
  // 고른 상태가 목록에서 사라졌으면('종료 포함' 끔 · 탭 전환) '전체'로 본다.
  const activeStatus: StatusFilter =
    statusFilter !== 'all' && rows.some((r) => r.status === statusFilter) ? statusFilter : 'all';
  const filtered = useMemo(() => {
    const byStatus =
      activeStatus === 'all' ? searched : searched.filter((r) => r.status === activeStatus);
    if (sortKey === 'oldest') return [...byStatus].reverse();
    if (sortKey === 'name') {
      return [...byStatus].sort((a, b) =>
        nameOf(a).localeCompare(nameOf(b), 'ko', { numeric: true }),
      );
    }
    return byStatus;
  }, [searched, activeStatus, sortKey]);
  const statusChips = STATUS_FILTER_ORDER.filter((st) => rows.some((r) => r.status === st));
  const truncated = (listQ.data?.total ?? 0) > rows.length;

  if (!canView) {
    return (
      <div style={deniedBlock}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          고객사 서류
        </h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
          이 페이지는 관리자, 매니저, 사업팀 매니저만 볼 수 있습니다.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* 2026-10-01 오너 요청 — 고객사 마스터와 같이, 큰 제목이 지금 탭(국내/해외)을 말한다. */}
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          고객사 서류 {scope === 'domestic' ? '국내' : '해외'}
        </h1>
        <div style={{ marginLeft: 'auto' }}>
          <Button type="button" disabled title="외부 협력업체 접근은 추후 제공 예정입니다.">
            외부 협력업체 링크 발급
          </Button>
        </div>
      </div>

      <div style={layout}>
        {/* 좌측 — 고객사 선택 (round11 요구 12: 검색 + 목록 클릭) */}
        <aside style={pickerColumn} aria-label="고객사 선택">
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>고객사 선택</div>
          {/* 추가수정(26-07-27) §2-2 — 국내/해외 탭 (고객사 마스터와 같은 모양·동작). */}
          <div role="tablist" aria-label="고객사 서류 국내/해외" style={scopeTabList}>
            <ScopeTabButton
              scopeKey="domestic"
              active={scope === 'domestic'}
              onActivate={() => setScope('domestic')}
            >
              국내
            </ScopeTabButton>
            <ScopeTabButton
              scopeKey="overseas"
              active={scope === 'overseas'}
              onActivate={() => setScope('overseas')}
            >
              해외
            </ScopeTabButton>
          </div>
          <input
            placeholder="상호나 사업자명으로 검색…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInput}
            data-testid="customer-search"
          />
          <label style={includeRetiredLabel}>
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
            />
            종료 포함
          </label>
          <div style={filterBar} role="group" aria-label="상태로 거르기">
            <FilterChip
              label="전체"
              count={searched.length}
              active={activeStatus === 'all'}
              onClick={() => setStatusFilter('all')}
            />
            {statusChips.map((st) => (
              <FilterChip
                key={st}
                label={CUSTOMER_MASTER_STATUS_LABEL[st]}
                count={statusCounts.get(st) ?? 0}
                active={activeStatus === st}
                color={CUSTOMER_MASTER_STATUS_COLOR[st]}
                onClick={() => setStatusFilter(st)}
              />
            ))}
          </div>
          <label style={sortLabel}>
            정렬
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              style={sortSelect}
              data-testid="cd-sort"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div style={listBox} role="listbox" aria-label="고객사 목록">
            {listQ.error ? (
              <div style={{ ...listEmpty, color: 'var(--fg-danger)' }}>
                {extractApiError(listQ.error)}
              </div>
            ) : listQ.isLoading ? (
              <div style={listEmpty}>불러오는 중…</div>
            ) : filtered.length === 0 ? (
              // 추가수정(26-07-27) §2-2 — 검색어 없이 목록이 비면 탭별 빈 상태
              // 문구, 검색 중이면 기존 문구 유지.
              <div style={listEmpty}>
                {search.trim()
                  ? '일치하는 고객사가 없습니다.'
                  : activeStatus !== 'all'
                    ? `'${CUSTOMER_MASTER_STATUS_LABEL[activeStatus]}' 고객사가 없습니다.`
                    : scope === 'domestic'
                      ? '국내 고객사가 없습니다.'
                      : '해외 고객사가 없습니다.'}
              </div>
            ) : (
              filtered.map((r) => (
                <CustomerRow
                  key={r.id}
                  row={r}
                  selected={customer?.id === r.id}
                  onSelect={() => setCustomer(r)}
                />
              ))
            )}
          </div>
          {/* 다계약 표시 차수3(26-08-05) ㉡ — 잘림 안내가 **무엇이 남고 무엇이 빠졌는지**
              말한다. 종전 문구('검색어로 좁혀주세요')는 할 수 없는 일을 시켰다: 검색은
              client-side 라 **받아 온 목록 안에서만** 찾으므로, 잘려 나간 고객사는 검색어를
              어떻게 넣어도 나오지 않는다. 어떤 순서로 받았는지까지 적어야 "왜 안 보이는지"를
              운영자가 알 수 있다. */}
          {truncated && (
            <div
              style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-muted)' }}
              data-testid="cd-list-truncated"
            >
              전체 {listQ.data?.total.toLocaleString()}곳 중{' '}
              <strong>등록이 최근인 {rows.length}곳</strong>만 받았습니다. 검색도 이 {rows.length}곳
              안에서만 찾습니다. 방금 등록한 고객사는 이 안에 있습니다.
            </div>
          )}
        </aside>

        {/* 우측 — 3 카테고리 */}
        <section style={docColumn} aria-label="고객사 서류 목록">
          {customer ? (
            <>
              {/* 2026-10-01 오너 요청 — 고른 고객사 이름을 서류 위에 크게(종전엔 왼쪽 목록 아래에
                  작게 있어 잘 안 보였다). 제목(고객사 서류, kuun-h2)보다 한 단계 작은 kuun-h3. */}
              <div style={selectedHeader} data-testid="cd-selected-customer">
                <h2 className="kuun-h3" style={{ margin: 0 }}>
                  {customer.displayName || customer.legalName || '(미입력)'}
                </h2>
                {customer.legalName && customer.legalName !== customer.displayName && (
                  <span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
                    {customer.legalName}
                  </span>
                )}
              </div>
              <DocumentSections customerId={customer.id} isAdmin={isAdmin} />
            </>
          ) : (
            <div style={emptyBlock}>좌측에서 고객사를 선택하면 서류가 표시됩니다.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  color,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color?: { fg: string; bg: string; border: string };
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        ...filterChip,
        color: active ? (color?.fg ?? 'var(--fg-brand)') : 'var(--fg-muted)',
        background: active ? (color?.bg ?? 'var(--bg-selected)') : 'transparent',
        borderColor: active ? (color?.border ?? 'var(--brand)') : 'var(--border-default)',
        fontWeight: active ? 600 : 500,
      }}
    >
      {label} <span style={{ opacity: 0.75 }}>{count}</span>
    </button>
  );
}

function CustomerRow({
  row,
  selected,
  onSelect,
}: {
  row: CustomerMasterRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const color = CUSTOMER_MASTER_STATUS_COLOR[row.status];
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      style={{
        ...listRow,
        background: selected ? 'var(--bg-selected)' : 'transparent',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={listRowName}>{row.displayName || row.legalName || '(미입력)'}</div>
        {row.legalName && row.legalName !== row.displayName && (
          <div style={listRowSub}>{row.legalName}</div>
        )}
      </div>
      <span
        style={{
          ...statusChip,
          color: color.fg,
          background: color.bg,
          borderColor: color.border,
        }}
      >
        {CUSTOMER_MASTER_STATUS_LABEL[row.status]}
      </span>
    </button>
  );
}

function DocumentSections({ customerId, isAdmin }: { customerId: string; isAdmin: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* 카드는 '서명 완료 문서' + '설치 사진' 둘만. 서명 완료 문서의 최종 PDF 는
          SignatureDocument.finalPdfS3Key(Attachment 아님)이라 별도 조회로 통합 표시. */}
      <SignedDocumentsSection customerId={customerId} isAdmin={isAdmin} />
      {/* 계획서(2026-08-27) 요구 3 — 회수한 로봇 사진. 회수 기록별로 묶어 보여 준다. */}
      <ReturnPhotosSection customerId={customerId} isAdmin={isAdmin} />
      {DOCUMENT_CATEGORIES.map((cat) => (
        <DocumentCategory
          key={cat.ownerType}
          ownerType={cat.ownerType}
          label={cat.label}
          customerId={customerId}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
}

// 서명 완료(COMPLETED) 문서 목록 + 최종 PDF + (관리자) 삭제·복구·완전삭제.
function SignedDocumentsSection({ customerId, isAdmin }: { customerId: string; isAdmin: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [hardTarget, setHardTarget] = useState<{ id: string; name: string } | null>(null);

  const q = useQuery({
    queryKey: ['signature-documents', 'completed', customerId, includeDeleted],
    queryFn: () =>
      signatureDocumentsApi.list({
        customerId,
        status: 'COMPLETED',
        includeDeleted: includeDeleted || undefined,
      }),
    enabled: !!customerId,
    staleTime: 60_000,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['signature-documents', 'completed', customerId] });

  const softDel = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success('문서를 삭제했습니다. 삭제 항목 표시를 켜면 복구할 수 있습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '삭제 실패'),
  });
  const restore = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.restore(id),
    onSuccess: () => {
      invalidate();
      toast.success('문서를 복구했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '복구 실패'),
  });
  const hardDel = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.hardDelete(id),
    onSuccess: () => {
      invalidate();
      setHardTarget(null);
      toast.success('문서를 완전삭제했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '완전삭제 실패'),
  });

  async function openPdf(id: string): Promise<void> {
    setOpening(id);
    try {
      const { url } = await signatureDocumentsApi.getPdfUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(extractApiError(err), '최종 PDF 열기 실패');
    } finally {
      setOpening(null);
    }
  }

  const items = q.data ?? [];

  return (
    <div style={categoryCard}>
      <div style={categoryHeader}>
        <strong style={{ fontSize: 13 }}>서명 완료 문서</strong>
        <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>({items.length}개)</span>
        {isAdmin && (
          <label style={inlineToggle}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            삭제 항목 표시
          </label>
        )}
      </div>
      {q.error ? (
        <div style={{ color: 'var(--fg-danger)', fontSize: 12, padding: 12 }}>
          {extractApiError(q.error)}
        </div>
      ) : q.isLoading ? (
        <div style={categoryEmpty}>불러오는 중…</div>
      ) : items.length === 0 ? (
        <div style={categoryEmpty}>서명이 완료된 문서가 여기에 표시됩니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((d) => (
            <div key={d.id} style={{ ...signedRow, opacity: d.deletedAt ? 0.6 : 1 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {d.displayName}
                  {d.deletedAt && <span style={deletedBadge}>삭제됨</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  로봇{' '}
                  {d.robots && d.robots.length > 0
                    ? d.robots.map((r) => r.serial ?? r.robotId).join(', ')
                    : (d.robotSerial ?? '–')}
                  {/* 추가수정 2차(26-07-27) 정정4 — 완료일은 한국시간 날짜만
                      (결정 ②A: 목록은 날짜만). 서명 문서 목록과 같은 표기·같은
                      폴백 문자('–')를 써서 한 문서가 화면마다 다른 날짜로 보이던
                      문제를 없앤다. 이전 toYmd 는 ISO 를 UTC 로 잘라, 한국
                      00:00~09:00 에 완료된 문서를 전날로 표시했다. */}
                  {d.completedAt ? ` · 완료 ${kstYmd(d.completedAt) ?? '–'}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Button
                  type="button"
                  onClick={() => void openPdf(d.id)}
                  disabled={opening === d.id}
                  title="최종 서명 PDF 열기"
                >
                  {opening === d.id ? '여는 중…' : 'PDF'}
                </Button>
                {isAdmin && !d.deletedAt && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => softDel.mutate(d.id)}
                    disabled={softDel.isPending}
                    title="삭제해도 나중에 복구할 수 있습니다"
                  >
                    삭제
                  </Button>
                )}
                {isAdmin && d.deletedAt && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => restore.mutate(d.id)}
                      disabled={restore.isPending}
                    >
                      복구
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => setHardTarget({ id: d.id, name: d.displayName })}
                    >
                      완전삭제
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!hardTarget}
        title="문서 완전삭제"
        message={`'${hardTarget?.name ?? ''}' 문서를 완전삭제하시겠습니까?\n되돌릴 수 없습니다. 이 문서로 만든 인수증이 있으면 삭제할 수 없습니다.`}
        danger
        confirmLabel="완전삭제"
        busy={hardDel.isPending}
        onConfirm={() => {
          if (hardTarget) hardDel.mutate(hardTarget.id);
        }}
        onCancel={() => setHardTarget(null)}
      />
    </div>
  );
}

function DocumentCategory({
  ownerType,
  label,
  customerId,
  isAdmin,
}: {
  ownerType: AttachmentOwnerType;
  label: string;
  customerId: string;
  isAdmin: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [hardTarget, setHardTarget] = useState<{ id: string; name: string } | null>(null);

  const q = useQuery({
    queryKey: ['attachments', ownerType, customerId, includeDeleted],
    queryFn: () => attachmentsApi.list(ownerType, customerId, includeDeleted),
    enabled: !!customerId,
    staleTime: 60_000,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['attachments', ownerType, customerId] });

  const softDel = useMutation({
    mutationFn: (id: string) => attachmentsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success('사진을 삭제했습니다. 삭제 항목 표시를 켜면 복구할 수 있습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '삭제 실패'),
  });
  const restore = useMutation({
    mutationFn: (id: string) => attachmentsApi.restore(id),
    onSuccess: () => {
      invalidate();
      toast.success('사진을 복구했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '복구 실패'),
  });
  const hardDel = useMutation({
    mutationFn: (id: string) => attachmentsApi.hardDelete(id),
    onSuccess: () => {
      invalidate();
      setHardTarget(null);
      toast.success('사진을 완전삭제했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '완전삭제 실패'),
  });

  const items = q.data ?? [];

  return (
    <div style={categoryCard}>
      <div style={categoryHeader}>
        <strong style={{ fontSize: 13 }}>{label}</strong>
        <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>({items.length}개)</span>
        {isAdmin && (
          <label style={inlineToggle}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            삭제 항목 표시
          </label>
        )}
      </div>
      {q.error ? (
        <div style={{ color: 'var(--fg-danger)', fontSize: 12, padding: 12 }}>
          {extractApiError(q.error)}
        </div>
      ) : q.isLoading ? (
        <div style={categoryEmpty}>불러오는 중…</div>
      ) : items.length === 0 ? (
        <div style={categoryEmpty}>필드팀이 현장 앱으로 올린 설치 사진이 여기에 표시됩니다.</div>
      ) : (
        <div style={fileGrid}>
          {items.map((a) => (
            <DocumentTile
              key={a.id}
              att={a}
              isAdmin={isAdmin}
              busy={softDel.isPending || restore.isPending}
              onSoftDelete={() => softDel.mutate(a.id)}
              onRestore={() => restore.mutate(a.id)}
              onHardDelete={() =>
                setHardTarget({ id: a.id, name: a.originalFilename ?? '(이름 없음)' })
              }
            />
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!hardTarget}
        title="사진 완전삭제"
        message={`'${hardTarget?.name ?? ''}' 사진을 완전삭제하시겠습니까?\n되돌릴 수 없으며 파일이 저장소에서도 제거됩니다.`}
        danger
        confirmLabel="완전삭제"
        busy={hardDel.isPending}
        onConfirm={() => {
          if (hardTarget) hardDel.mutate(hardTarget.id);
        }}
        onCancel={() => setHardTarget(null)}
      />
    </div>
  );
}

// ============================================================================
// 회수 로봇 사진 (계획서 2026-08-27 요구 3 / 결정 4)
// ============================================================================
//
// 사진은 **회수 기록(Deployment)** 에 붙는다 — 고객사에 통째로 붙이면 여러 번
// 회수한 고객사에서 "어느 로봇의 어느 회수인지"를 나중에 가를 수 없다. 그래서 이
// 카드는 고객사의 **회수된 배치**를 줄로 세우고, 줄마다 그 회수의 사진을 보여 준다.
//
// 두 갈래로 들어온다:
//   ① 회수 창에서 회수하면서 바로(선택)
//   ② 여기서 나중에 — 회수 당시 못 찍었거나 업로드가 실패한 경우(수동 추가)
//
// ⚠ 회수 건마다 사진 목록을 따로 조회한다. 회수가 아주 많은 고객사에서는 호출이
//   그만큼 늘어난다. 지금 운영 데이터(고객사당 회수 한 자릿수)에서는 문제가 없고,
//   서버에 목록을 합쳐 주는 칸을 새로 만드는 것은 별도 차수로 남긴다.
function ReturnPhotosSection({ customerId, isAdmin }: { customerId: string; isAdmin: boolean }) {
  const depQ = useQuery({
    queryKey: ['deployments', { customerId, forReturnPhotos: true }],
    queryFn: () => deploymentsApi.list({ customerId, pageSize: 100 }),
    enabled: !!customerId,
    staleTime: 60_000,
  });

  // 회수된 배치만 — 아직 나가 있는 로봇에는 '회수 사진'이 없다.
  const returned = (depQ.data?.items ?? [])
    .filter((d) => d.returnedAt)
    .sort((a, b) => (b.returnedAt ?? '').localeCompare(a.returnedAt ?? ''));

  return (
    <div style={categoryCard} data-testid="return-photos-card">
      <div style={categoryHeader}>
        <strong style={{ fontSize: 13 }}>회수 로봇 사진</strong>
        <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>(회수 {returned.length}건)</span>
      </div>
      {depQ.error ? (
        <div style={{ color: 'var(--fg-danger)', fontSize: 12, padding: 12 }}>
          {extractApiError(depQ.error)}
        </div>
      ) : depQ.isLoading ? (
        <div style={categoryEmpty}>불러오는 중…</div>
      ) : returned.length === 0 ? (
        <div style={categoryEmpty}>회수된 로봇이 아직 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10 }}>
          {returned.map((d) => (
            <ReturnPhotoGroup key={d.id} deployment={d} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

/** 회수 한 건(그 로봇의 그 회수)의 사진 묶음 + 수동 추가. */
function ReturnPhotoGroup({
  deployment,
  isAdmin,
}: {
  deployment: DeploymentSummaryDto;
  isAdmin: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [hardTarget, setHardTarget] = useState<{ id: string; name: string } | null>(null);

  const ownerType = AttachmentOwnerType.DEPLOYMENT_RETURN;
  const q = useQuery({
    queryKey: ['attachments', ownerType, deployment.id, false],
    queryFn: () => attachmentsApi.list(ownerType, deployment.id, false),
    staleTime: 60_000,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['attachments', ownerType, deployment.id] });

  const softDel = useMutation({
    mutationFn: (id: string) => attachmentsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success('사진을 삭제했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '삭제 실패'),
  });
  const hardDel = useMutation({
    mutationFn: (id: string) => attachmentsApi.hardDelete(id),
    onSuccess: () => {
      invalidate();
      setHardTarget(null);
      toast.success('사진을 완전삭제했습니다.');
    },
    onError: (e) => toast.error(extractApiError(e), '완전삭제 실패'),
  });

  async function addPhotos(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setUploading(true);
    const failed: string[] = [];
    for (const file of Array.from(files)) {
      try {
        await uploadAttachment(file, { ownerType, ownerId: deployment.id });
      } catch {
        failed.push(file.name);
      }
    }
    setUploading(false);
    invalidate();
    // 몇 장 중 몇 장이 실패했는지 말한다 — "업로드 실패" 한 줄로 뭉치면 어느 파일을
    // 다시 골라야 하는지 알 수 없다.
    if (failed.length === 0) toast.success(`사진 ${files.length}장을 올렸습니다.`);
    else toast.error(`${failed.length}장을 올리지 못했습니다: ${failed.join(', ')}`, '업로드 실패');
  }

  const items = q.data ?? [];
  const sn = deployment.robot?.serialShort
    ? formatRobotShortId(deployment.robot.serialShort)
    : (deployment.robot?.serialLong ?? deployment.robotId.slice(0, 8));

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <strong style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{sn}</strong>
        <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
          회수일 {kstYmd(deployment.returnedAt) ?? '–'}
        </span>
        <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>· 사진 {items.length}장</span>
        {isAdmin && (
          <label
            style={{ marginLeft: 'auto', fontSize: 11, cursor: 'pointer' }}
            data-testid={`return-photo-add-${deployment.id}`}
          >
            {uploading ? '올리는 중…' : '사진 추가'}
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              style={{ display: 'none' }}
              onChange={(e) => {
                void addPhotos(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </div>
      {q.isLoading ? (
        <div style={categoryEmpty}>불러오는 중…</div>
      ) : items.length === 0 ? (
        <div style={{ ...categoryEmpty, padding: 8 }}>
          이 회수의 사진이 없습니다.{isAdmin ? " '사진 추가'로 올릴 수 있습니다." : ''}
        </div>
      ) : (
        <div style={fileGrid}>
          {items.map((a) => (
            <DocumentTile
              key={a.id}
              att={a}
              isAdmin={isAdmin}
              busy={softDel.isPending}
              onSoftDelete={() => softDel.mutate(a.id)}
              onRestore={() => undefined}
              onHardDelete={() =>
                setHardTarget({ id: a.id, name: a.originalFilename ?? '(이름 없음)' })
              }
            />
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!hardTarget}
        title="사진 완전삭제"
        message={`'${hardTarget?.name ?? ''}' 사진을 완전삭제하시겠습니까?\n되돌릴 수 없으며 파일이 저장소에서도 제거됩니다.`}
        danger
        confirmLabel="완전삭제"
        busy={hardDel.isPending}
        onConfirm={() => {
          if (hardTarget) hardDel.mutate(hardTarget.id);
        }}
        onCancel={() => setHardTarget(null)}
      />
    </div>
  );
}

function DocumentTile({
  att,
  isAdmin,
  busy,
  onSoftDelete,
  onRestore,
  onHardDelete,
}: {
  att: AttachmentDto;
  isAdmin: boolean;
  busy: boolean;
  onSoftDelete: () => void;
  onRestore: () => void;
  onHardDelete: () => void;
}) {
  const isImage = att.contentType.startsWith('image/');
  return (
    <div style={{ ...tile, opacity: att.deletedAt ? 0.6 : 1 }}>
      <a
        href={att.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
      >
        {isImage ? (
          <img src={att.downloadUrl} alt={att.originalFilename ?? '첨부'} style={thumb} />
        ) : (
          <div style={fileIcon} aria-hidden>
            <Icon name="file" size={36} color="var(--fg-subtle)" />
          </div>
        )}
        <div style={fileName} title={att.originalFilename ?? ''}>
          {att.originalFilename ?? '(이름 없음)'}
          {att.deletedAt ? ' · 삭제됨' : ''}
        </div>
      </a>
      {isAdmin && (
        <div style={tileActions}>
          {!att.deletedAt ? (
            <button type="button" style={tileBtn} onClick={onSoftDelete} disabled={busy}>
              삭제
            </button>
          ) : (
            <>
              <button type="button" style={tileBtn} onClick={onRestore} disabled={busy}>
                복구
              </button>
              <button type="button" style={tileBtnDanger} onClick={onHardDelete}>
                완전삭제
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const layout: CSSProperties = {
  // 추가수정(26-07-27) §2-1 — 목록을 화면 높이에 맞게. minHeight 0 이어야 flex
  // 부모(페이지 루트) 안에서 줄어들 수 있다. alignItems:'start' 는 제거 — 남으면
  // grid 아이템이 stretch 되지 않아 flex:1 이 내용 높이 기준으로 무력화된다.
  flex: 1,
  minHeight: 0,
  display: 'grid',
  gridTemplateColumns: '280px 1fr',
  gap: 16,
};
const pickerColumn: CSSProperties = {
  // 추가수정(26-07-27) §2-1 — 세로 flex 로 listBox 가 남는 높이를 전부 차지.
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  padding: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
// 추가수정(26-07-27) §2-2 — 탭 바 (CustomerMasterPage 와 동일 모양).
const scopeTabList: CSSProperties = {
  display: 'flex',
  gap: 4,
  marginBottom: 8,
  borderBottom: '1px solid var(--border-subtle)',
};
const searchInput: CSSProperties = {
  width: '100%',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
const includeRetiredLabel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 8,
  fontSize: 12,
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const listBox: CSSProperties = {
  marginTop: 8,
  // 추가수정(26-07-27) §2-1 — maxHeight 420 고정 제거. 남는 높이를 전부 채우고
  // 목록만 내부 스크롤한다(모니터가 클수록 한 번에 보이는 고객사가 많아짐).
  flex: 1,
  overflowY: 'auto',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-sunken)',
};
const listEmpty: CSSProperties = {
  padding: 12,
  fontSize: 12,
  color: 'var(--fg-muted)',
};
const listRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  padding: '6px 8px',
  border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  textAlign: 'left',
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
};
const listRowName: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const listRowSub: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const statusChip: CSSProperties = {
  flexShrink: 0,
  padding: '1px 6px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid',
  fontSize: 10,
  whiteSpace: 'nowrap',
};
const selectedHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 12,
};
const filterBar: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginBottom: 8,
};
const filterChip: CSSProperties = {
  padding: '2px 8px',
  borderRadius: 999,
  border: '1px solid',
  fontSize: 11,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  lineHeight: 1.6,
};
const sortLabel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  marginBottom: 8,
};
const sortSelect: CSSProperties = {
  flex: 1,
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};
const docColumn: CSSProperties = {
  minWidth: 0,
  // 추가수정(26-07-27) §2-1 — 사진 많은 고객사에서 페이지 전체(AppShell main)가
  // 다시 스크롤되지 않도록, 우측 서류 영역도 같은 높이 규칙 + 내부 스크롤.
  minHeight: 0,
  overflowY: 'auto',
};
const emptyBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 13,
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
const categoryCard: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  overflow: 'hidden',
};
const categoryHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-subtle)',
  background: 'var(--bg-sunken)',
};
const categoryEmpty: CSSProperties = {
  padding: 16,
  fontSize: 12,
  color: 'var(--fg-muted)',
};
// round6 §1-6(f) — 서명 완료 문서 행.
const signedRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-subtle)',
};
const inlineToggle: CSSProperties = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 11,
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const deletedBadge: CSSProperties = {
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
};
const tileActions: CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: '4px',
  borderTop: '1px solid var(--border-subtle)',
  justifyContent: 'flex-end',
};
const tileBtn: CSSProperties = {
  fontSize: 10,
  padding: '2px 6px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};
const tileBtnDanger: CSSProperties = {
  ...tileBtn,
  color: 'var(--fg-danger)',
  borderColor: 'color-mix(in srgb, var(--fg-danger) 40%, transparent)',
};
const fileGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: 8,
  padding: 12,
};
const tile: CSSProperties = {
  display: 'block',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  overflow: 'hidden',
  background: 'var(--bg-sunken)',
  textDecoration: 'none',
  color: 'inherit',
};
const thumb: CSSProperties = {
  width: '100%',
  height: 100,
  objectFit: 'cover',
  display: 'block',
};
const fileIcon: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px 0',
};
const fileName: CSSProperties = {
  fontSize: 10,
  padding: 4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderTop: '1px solid var(--border-subtle)',
};
const deniedBlock: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 24,
};
